import { sdk, SALES_CHANNEL_ID } from '@/lib/medusa';
import { getRegion } from './regions';

const NO_SHOP_TAG = 'Unverkäuflich';

const FIELDS_LIST =
  'id,handle,title,subtitle,description,thumbnail,created_at,collection_id,*images,*tags,*type,*variants,*variants.calculated_price,+variants.inventory_quantity,metadata';

const FIELDS_DETAIL =
  'id,handle,title,subtitle,description,thumbnail,material,weight,length,height,width,origin_country,*collection,*images,*tags,*type,*variants,*variants.calculated_price,+variants.inventory_quantity,*variants.options,*options,*options.values,metadata,shipping_profile.id';

function withSalesChannel(query = {}) {
  if (SALES_CHANNEL_ID) {
    return { ...query, sales_channel_id: SALES_CHANNEL_ID };
  }
  return query;
}

async function withRegion(query = {}, countryCode) {
  const region = await getRegion(countryCode);
  if (region) {
    return { ...query, region_id: region.id };
  }
  return query;
}

let productTypeMapPromise = null;

async function getProductTypeIdByValue(value) {
  if (!value) return null;
  if (!productTypeMapPromise) {
    productTypeMapPromise = sdk.client
      .fetch('/store/product-types', {
        method: 'GET',
        query: { fields: 'id,value', limit: 100 },
        next: { tags: ['product-types'], revalidate: 3600 },
      })
      .then((res) => {
        const map = new Map();
        for (const t of res.product_types || []) map.set(t.value, t.id);
        return map;
      })
      .catch((e) => {
        console.error('[products] product-types fetch failed:', e);
        productTypeMapPromise = null;
        return new Map();
      });
  }
  const map = await productTypeMapPromise;
  return map.get(value) || null;
}

/**
 * Return the distinct product type values (e.g. "Objekte", "Zeichnungen",
 * "Schalen", "Schmuck") as defined in Medusa. Used to build the portfolio
 * category tabs from a single source of truth (the product `Typ`).
 */
export async function getProductTypes() {
  try {
    const res = await sdk.client.fetch('/store/product-types', {
      method: 'GET',
      query: { fields: 'id,value', limit: 100 },
      next: { tags: ['product-types'], revalidate: 3600 },
    });
    return (res.product_types || []).map((t) => t.value).filter(Boolean);
  } catch (e) {
    console.error('[products] product-types list failed:', e);
    return [];
  }
}

/**
 * Fetch products for the storefront. Supports filtering by product type
 * (Objekte / Zeichnungen / Schmuck) or by the absence of the `Unverkäuflich`
 * tag for the "Shop" tab.
 */
export async function listProducts({
  typeId,
  typeValue,
  shopOnly = false,
  limit = 100,
  offset = 0,
  countryCode,
} = {}) {
  let query = { limit, offset, fields: FIELDS_LIST };
  query = withSalesChannel(query);
  query = await withRegion(query, countryCode);

  if (typeId) {
    query.type_id = typeId;
  } else if (typeValue) {
    const resolved = await getProductTypeIdByValue(typeValue);
    if (resolved) query.type_id = resolved;
    else return { products: [], count: 0 };
  }

  try {
    const { products, count } = await sdk.client.fetch('/store/products', {
      query,
      next: { tags: ['products'], revalidate: 60 },
    });
    let items = products || [];
    if (shopOnly) {
      items = items.filter(
        (p) => !(p.tags || []).some((t) => t.value === NO_SHOP_TAG)
      );
    }
    return { products: items, count: count ?? items.length };
  } catch (e) {
    console.error('[products] list failed:', e);
    return { products: [], count: 0 };
  }
}

export async function getProductByHandle(handle, { countryCode } = {}) {
  if (!handle) return null;
  let query = { handle, fields: FIELDS_DETAIL, limit: 1 };
  query = withSalesChannel(query);
  query = await withRegion(query, countryCode);

  try {
    const { products } = await sdk.client.fetch('/store/products', {
      query,
      next: { tags: ['products', `product-${handle}`], revalidate: 60 },
    });
    return products?.[0] ?? null;
  } catch (e) {
    console.error('[products] detail failed:', e);
    return null;
  }
}

export async function getProductById(id, { countryCode } = {}) {
  if (!id) return null;
  let query = { fields: FIELDS_DETAIL };
  query = withSalesChannel(query);
  query = await withRegion(query, countryCode);

  try {
    const { product } = await sdk.client.fetch(`/store/products/${id}`, {
      query,
      next: { tags: ['products', `product-id-${id}`], revalidate: 60 },
    });
    return product ?? null;
  } catch (e) {
    console.error('[products] by-id failed:', e);
    return null;
  }
}

export function isNoShop(product) {
  return (product?.tags || []).some((t) => t.value === NO_SHOP_TAG);
}

export function getCalculatedAmount(product) {
  const v = product?.variants?.[0];
  return v?.calculated_price?.calculated_amount ?? null;
}

export function getCurrencyCode(product) {
  const v = product?.variants?.[0];
  return v?.calculated_price?.currency_code ?? 'eur';
}

export function getVariantAmount(variant) {
  return variant?.calculated_price?.calculated_amount ?? null;
}

export function getVariantCurrency(variant) {
  return variant?.calculated_price?.currency_code ?? 'eur';
}

/**
 * Resolve the pricing of a variant including any active price list (sale).
 * Medusa exposes `calculated_amount` (the price the customer pays, after a sale
 * price list is applied) and `original_amount` (the base price). When the
 * calculated price is lower, the variant is on sale and the storefront shows the
 * original price struck through next to the reduced one.
 */
export function getPriceInfo(variant) {
  const cp = variant?.calculated_price;
  if (!cp) {
    return { amount: null, originalAmount: null, onSale: false, currencyCode: 'eur' };
  }
  const amount = cp.calculated_amount ?? null;
  const originalAmount = cp.original_amount ?? amount;
  const onSale =
    amount != null &&
    originalAmount != null &&
    Number(amount) < Number(originalAmount);
  return {
    amount,
    originalAmount,
    onSale,
    currencyCode: cp.currency_code ?? 'eur',
  };
}

/**
 * Fetch the active *automatic* promotions exposed by the custom store route
 * `/store/active-promotions`. These are needed to preview a discounted price on
 * the shop grid / product page, because automatic promotions are applied by
 * Medusa at the cart level and therefore never lower a variant's
 * `calculated_amount` (unlike sale price lists, which do).
 *
 * The cart remains the single source of truth: it re-applies the same
 * promotions, so whatever is previewed here is what the customer is charged.
 * Returns `[]` on any error so the storefront simply falls back to full prices.
 */
export async function getActivePromotions() {
  try {
    const res = await sdk.client.fetch('/store/active-promotions', {
      method: 'GET',
      next: { tags: ['promotions'], revalidate: 60 },
    });
    return res?.promotions || [];
  } catch (e) {
    console.error('[products] active promotions failed:', e);
    return [];
  }
}

// Resolve the value(s) of a promotion target-rule attribute for a given
// product/variant. Returns `null` when the attribute references data we don't
// load (e.g. categories) so the caller can skip the preview instead of guessing.
function resolveTargetAttribute(product, variant, attribute) {
  switch (attribute) {
    case 'items.product.id':
      return [product?.id];
    case 'items.product.type_id':
      return [product?.type_id ?? product?.type?.id];
    case 'items.product.collection_id':
      return [product?.collection_id ?? product?.collection?.id];
    case 'items.product.tags.id':
      return (product?.tags || []).map((tag) => tag?.id);
    case 'items.variant.id':
      return variant ? [variant.id] : (product?.variants || []).map((v) => v.id);
    case 'items.variant.sku':
      return variant?.sku
        ? [variant.sku]
        : (product?.variants || []).map((v) => v.sku);
    default:
      return null;
  }
}

// Evaluate a single target rule. Returns true/false, or `null` when it can't be
// evaluated confidently (unknown attribute/operator) — in which case the promo
// is not previewed (the cart will still apply it correctly).
function evaluateTargetRule(product, variant, rule) {
  const actual = resolveTargetAttribute(product, variant, rule?.attribute);
  if (actual === null) return null;
  const expected = (rule?.values || []).map((v) => String(v));
  const present = actual
    .filter((v) => v != null)
    .map((v) => String(v))
    .some((v) => expected.includes(v));
  const op = rule?.operator || 'eq';
  if (op === 'eq' || op === 'in') return present;
  if (op === 'ne' || op === 'not_in') return !present;
  return null;
}

// All target rules must match (AND). No rules => applies to every item.
// Returns `null` if any rule can't be evaluated, so the caller skips the preview.
function matchesTargetRules(product, variant, rules) {
  if (!rules || rules.length === 0) return true;
  let result = true;
  for (const rule of rules) {
    const outcome = evaluateTargetRule(product, variant, rule);
    if (outcome === null) return null;
    if (!outcome) result = false;
  }
  return result;
}

// Cart-level rules (currency, min total, customer group, …). For a per-product
// preview we can only safely honour `currency_code`; any other condition means
// the discount depends on the whole cart, so we don't preview it.
function cartRulesSatisfiableForPreview(rules, currency) {
  if (!rules || rules.length === 0) return true;
  return rules.every((rule) => {
    if (rule?.attribute !== 'currency_code') return false;
    const expected = (rule?.values || []).map((v) => String(v).toLowerCase());
    const op = rule?.operator || 'eq';
    const present = expected.includes(currency);
    if (op === 'ne' || op === 'not_in') return !present;
    return present;
  });
}

// Per-unit discount a promotion takes off the given amount. `fixed`/`each` is
// exact; `fixed`/`across` is treated as the per-item cap (correct for the
// single-item buy-now flow). Clamped so a price never goes negative.
function unitDiscountFor(amount, applicationMethod) {
  const value = Number(applicationMethod?.value) || 0;
  if (value <= 0) return 0;
  if (applicationMethod.type === 'percentage') {
    return Math.min(amount, (amount * value) / 100);
  }
  if (applicationMethod.type === 'fixed') {
    return Math.min(amount, value);
  }
  return 0;
}

// Largest item-level automatic discount that applies to this product/variant.
function bestPreviewDiscount(amount, currency, product, variant, promotions) {
  let best = 0;
  for (const promo of promotions || []) {
    const am = promo?.application_method;
    if (!am) continue;
    // Only item-level discounts can be priced per product. Order-level and
    // shipping discounts (and buyget) are reflected in the cart, not here.
    if (am.target_type !== 'items') continue;
    if (am.currency_code && String(am.currency_code).toLowerCase() !== currency) {
      continue;
    }
    if (!cartRulesSatisfiableForPreview(promo.rules, currency)) continue;
    if (matchesTargetRules(product, variant, am.target_rules) !== true) continue;
    const discount = unitDiscountFor(amount, am);
    if (discount > best) best = discount;
  }
  return best;
}

/**
 * Fold any applicable item-level automatic promotion into a base price-info
 * object (from `getPriceInfo`). The original list price stays as the struck
 * reference, the promotion lowers the payable amount, and `onSale` becomes true
 * so the existing PriceTag UI renders the reduction automatically.
 */
export function applyPromotionsToPriceInfo(info, product, variant, promotions) {
  if (!info || info.amount == null || !promotions?.length) return info;
  const base = Number(info.amount);
  const currency = (info.currencyCode || 'eur').toLowerCase();
  const discount = bestPreviewDiscount(base, currency, product, variant, promotions);
  if (discount <= 0) return info;
  const finalAmount = Math.max(0, base - discount);
  const original = Math.max(Number(info.originalAmount ?? base), base);
  return {
    ...info,
    amount: finalAmount,
    originalAmount: original,
    onSale: finalAmount < original,
    promoApplied: true,
  };
}

/**
 * Price info for a specific variant including price-list sales AND active
 * automatic promotions. Pass the product so promotions targeting the product
 * (id / type / collection / tags) can be resolved.
 */
export function getVariantPriceInfo(product, variant, promotions) {
  return applyPromotionsToPriceInfo(
    getPriceInfo(variant),
    product,
    variant,
    promotions
  );
}

export function getProductPriceInfo(product, promotions) {
  const variant = product?.variants?.[0];
  return applyPromotionsToPriceInfo(
    getPriceInfo(variant),
    product,
    variant,
    promotions
  );
}

/**
 * Final displayed amount for a product (after sale + promotion), used for
 * sorting / price-bucket filtering so the shop reflects what the customer sees.
 */
export function getDisplayAmount(product, promotions) {
  const info = getProductPriceInfo(product, promotions);
  return info?.amount ?? null;
}

/**
 * Resolve the gallery image associated with a specific variant. The colorway
 * image is stored in `variant.metadata.image` (falling back to the variant
 * thumbnail) so the storefront can jump the gallery to the selected variant.
 */
export function getVariantImage(variant) {
  return variant?.metadata?.image || variant?.thumbnail || null;
}

/**
 * Return the product options a customer can actually choose from, excluding
 * Medusa's implicit "Default option" used by single-variant products.
 */
export function getSelectableOptions(product) {
  return (product?.options || []).filter(
    (o) => o?.title && o.title.trim().toLowerCase() !== 'default option'
  );
}

export function formatPrice(amount, currencyCode = 'eur', locale = 'de') {
  if (amount == null || Number.isNaN(Number(amount))) return null;
  const value = Number(amount);
  const intlLocale = locale === 'de' ? 'de-DE' : 'en-DE';
  // Whole amounts show no decimals ("120 €"), amounts with cents show two
  // ("8,90 €") — never a lone trailing digit like "8,9 €".
  const fractionDigits = Number.isInteger(value) ? 0 : 2;
  const cur = (currencyCode || 'eur').toLowerCase();

  // German convention places the symbol after the amount ("8,90 €"). We format
  // the bare number and append the symbol so the placement is identical in both
  // locales, regardless of what Intl's currency style would default to.
  if (cur === 'eur') {
    const num = new Intl.NumberFormat(intlLocale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: 2,
    }).format(value);
    return `${num} €`;
  }

  try {
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency: cur.toUpperCase(),
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${cur.toUpperCase()}`;
  }
}
